import {GaiaRed} from '@gaia/colors';
import {outlinedIconHeight, outlinedIconWidth, pinHeight, pinWidth} from '@gaia/icons/waypoints';
import {createSvgBlobUrl} from '@gaia/utils/createSvgBlobUrl';
import {
    getMarkerDecoration,
    getOutlinedIconMarkerImg,
    getPinSvg,
    mapboxImagePixelRatio,
} from '@gaia/utils/gaia/getMapboxImages';
import {useEffect, useRef} from 'react';

type IconSize = 'small' | 'large';

interface WaypointIconProps extends Omit<React.HTMLProps<HTMLDivElement>, 'size'> {
    markerType: MarkerType;
    markerDecoration: string;
    markerColor: GaiaColorHex | undefined;
    size: IconSize;
}

const pinDisplayWidth = pinWidth / mapboxImagePixelRatio;
const pinDisplayHeight = pinHeight / mapboxImagePixelRatio;
const outlinedIconDisplayWidth = outlinedIconWidth / mapboxImagePixelRatio;
const outlinedIconDisplayHeight = outlinedIconHeight / mapboxImagePixelRatio;

const sizes: Record<IconSize, Record<'width' | 'height' | 'pinWidth' | 'pinHeight', number>> = {
    small: {
        width: outlinedIconDisplayWidth * 0.75,
        height: outlinedIconDisplayHeight * 0.75,
        pinWidth: pinDisplayWidth * 0.825,
        pinHeight: pinDisplayHeight * 0.825,
    },
    large: {
        width: outlinedIconDisplayWidth,
        height: outlinedIconDisplayHeight,
        pinWidth: pinDisplayWidth,
        pinHeight: pinDisplayHeight,
    },
};

export default function WaypointIcon({
    markerColor,
    markerDecoration,
    markerType,
    size,
    ...props
}: WaypointIconProps) {
    const dims = sizes[size];

    return (
        <div {...props}>
            {markerType === 'outlined-icon' ? (
                <WaypointIconCanvas
                    width={dims.width}
                    height={dims.height}
                    markerDecoration={markerDecoration}
                    markerColor={markerColor}
                />
            ) : (
                <img
                    width={dims.pinWidth}
                    height={dims.pinHeight}
                    src={createSvgBlobUrl(getPinSvg(markerColor ?? GaiaRed))}
                    alt={markerType}
                    style={{display: 'block'}}
                />
            )}
        </div>
    );
}

interface WaypointIconCanvasProps extends React.HTMLProps<HTMLCanvasElement> {
    markerDecoration: string;
    markerColor: GaiaColorHex | undefined;
}

function WaypointIconCanvas({
    markerDecoration,
    markerColor,
    width,
    height,
}: WaypointIconCanvasProps) {
    const canvasRef = useRef<HTMLCanvasElement>(null);

    // Resize and paint Emoji waypoint icon
    useEffect(() => {
        if (!canvasRef.current) {
            return;
        }

        const ctx = canvasRef.current.getContext('2d')!;

        if (!markerColor) {
            getMarkerDecoration(markerDecoration)
                .then((imageData) => {
                    if (imageData instanceof ImageData) {
                        ctx.putImageData(imageData, 0, 0);
                    }
                })
                .catch(console.warn);
        } else {
            getOutlinedIconMarkerImg(markerDecoration, markerColor)
                .then((imageData) => ctx.putImageData(imageData, 0, 0))
                .catch(console.warn);
        }
    }, [markerDecoration, markerColor]);

    // The width and height props dictate the size of canvas' drawing area.
    // The width and height style props dictate the size of the canvas when rendered.
    // The drawing area is automatically scaled to make the styled width and height.
    return (
        <canvas
            ref={canvasRef}
            width={outlinedIconWidth}
            height={outlinedIconHeight}
            style={{width, height}}
        />
    );
}
